import type { Label } from "@app/core";
import { Button, Trash2 } from "@app/ui";
import { useState } from "react";

import { orderByIds } from "../../lib/apply-order.js";
import { Reorder } from "../../lib/motion/index.js";
import { useDragClickGuard } from "../../lib/useDragClickGuard.js";
import { useI18n } from "../../lib/useI18n.js";
import { useLabelsStore, useSettingsStore } from "../../stores/index.js";
import { AddEntityRow } from "./AddEntityRow.js";
import { DeleteEntityDialog } from "./DeleteEntityDialog.js";
import { EntityRow } from "./EntityRow.js";

/** Organize → Labels: flat drag-reorderable list (persisted `labelOrder`).
 *  Label color lives on the label record; the palette board writes it via the
 *  `label` board-target kind. No nesting, no per-view visibility, no emoji —
 *  color and delete are the only controls, both always inline on the row. */
export function LabelsPanel() {
  const { t } = useI18n();
  const labels = useLabelsStore((s) => s.labels);
  const add = useLabelsStore((s) => s.add);
  const rename = useLabelsStore((s) => s.rename);
  const setColor = useLabelsStore((s) => s.setColor);
  const remove = useLabelsStore((s) => s.remove);
  const labelOrder = useSettingsStore((s) => s.labelOrder);
  const setLabelOrder = useSettingsStore((s) => s.setLabelOrder);
  const [confirming, setConfirming] = useState<Label | null>(null);
  const guard = useDragClickGuard();

  const ordered = orderByIds(labels, labelOrder, (label) => label.id);

  return (
    <div>
      <Reorder.Group
        as="div"
        axis="y"
        values={ordered.map((label) => label.id)}
        onReorder={(next) => void setLabelOrder(next as string[])}
      >
        {ordered.map((label) => (
          <EntityRow
            key={label.id}
            id={label.id}
            guard={guard}
            swatchHex={label.color ?? "#888888"}
            colorTarget={{ kind: "label", id: label.id }}
            colorOverridden={label.color != null}
            onRevertColor={() => void setColor(label.id, null)}
            name={label.name}
            onRename={(name) => void rename(label.id, name)}
            deleteAction={
              <Button type="button" size="sm" variant="ghost" onClick={() => setConfirming(label)} title={t("lists.deleteLabelTitle")} aria-label={t("lists.deleteLabelTitle")}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            }
          />
        ))}
      </Reorder.Group>

      <AddEntityRow placeholder={t("lists.newLabel")} onAdd={async (name) => { await add(name); }} />
      <DeleteEntityDialog
        name={confirming?.name}
        body={t("lists.deleteLabelBody")}
        confirmLabel={t("lists.deleteLabelTitle")}
        onConfirm={() => {
          const label = confirming;
          setConfirming(null);
          if (label) void remove(label.id);
        }}
        onClose={() => setConfirming(null)}
      />
    </div>
  );
}
